import * as Sentry from '@sentry/react';
import { createContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/shared/lib/supabase';
import { trackEvent, identifyUser, resetAnalyticsUser } from '@/shared/lib/analytics';
import type { AuthContextType } from '../types';

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Failed to get session:', error.message);
        Sentry.captureException(error);
      }
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    }).catch((err) => {
      console.error('Session fetch failed:', err);
      Sentry.captureException(err);
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (event === 'SIGNED_IN' && session?.user) {
        const { id, created_at, last_sign_in_at, app_metadata } = session.user;
        const provider = app_metadata?.provider ?? 'email';

        // Identify the user in PostHog so subsequent events are linked
        identifyUser(id, {
          email: session.user.email,
          created_at: session.user.created_at,
        });

        // Distinguish new sign-ups from returning sign-ins (within 60s of account creation)
        const isNewUser =
          created_at &&
          last_sign_in_at &&
          Math.abs(new Date(last_sign_in_at).getTime() - new Date(created_at).getTime()) < 60_000;

        if (isNewUser) {
          const signupKey = `safhati_signup_tracked_${id}`;
          if (!localStorage.getItem(signupKey)) {
            localStorage.setItem(signupKey, '1');
            const urlParams = new URLSearchParams(window.location.search);
            trackEvent('user_signed_up', {
              provider,
              referrer: document.referrer || 'direct',
              utm_source: urlParams.get('utm_source') ?? undefined,
              utm_medium: urlParams.get('utm_medium') ?? undefined,
              utm_campaign: urlParams.get('utm_campaign') ?? undefined,
            });

            // Google Ads signup conversion
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'sign_up', { method: provider });
            }
          }
        } else {
          trackEvent('user_signed_in', { provider });
        }
      }

      if (event === 'SIGNED_OUT') {
        trackEvent('user_signed_out', {});
        resetAnalyticsUser();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password, options: { captchaToken } });
    return { error };
  }, []);

  const signUpWithEmail = useCallback(async (email: string, password: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        captchaToken,
      },
    });
    return { error };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          prompt: 'select_account',
        },
      },
    });
    return { error: error as AuthError | null };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  }, []);

  const resetPassword = useCallback(async (email: string, captchaToken?: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
      captchaToken,
    });
    return { error };
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error };
  }, []);

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isAuthenticated: !!session,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
